import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Tooltip, Checkbox, Row, Col, InputNumber, Tag } from 'antd';
import { PlusOutlined, ReloadOutlined, PercentageOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { commissionPlansApi, productsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatPriceToman } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const TYPE_LABELS = {
  progressive_tiers: 'پلکانی واقعی (هر بخش با نرخ خودش)',
  fixed_percentage: 'درصد نهایی روی کل فروش',
};

// همان منطق بک‌اند (calculateDealCommission) برای نمایش شبیه‌سازی
const simulateCommission = (tiers, sales) => {
  const list = (tiers || [])
    .filter((t) => t && t.min_amount !== undefined && t.min_amount !== null && t.percentage !== undefined && t.percentage !== null)
    .map((t) => ({
      min: Number(t.min_amount) || 0,
      max: t.max_amount ? Number(t.max_amount) : Infinity,
      rate: Number(t.percentage) || 0,
      bonus: Number(t.fixed_bonus) || 0,
    }))
    .sort((a, b) => a.min - b.min);

  const rows = [];
  let progressive = 0;
  list.forEach((t) => {
    if (sales > t.min) {
      const part = Math.min(sales, t.max) - t.min;
      if (part > 0) {
        const c = (part * t.rate) / 100;
        progressive += c;
        rows.push({ ...t, part, commission: c });
      }
    }
  });

  const matched = list.find((t) => sales >= t.min && sales <= t.max);
  const flat = matched ? (sales * matched.rate) / 100 + matched.bonus : 0;

  return { progressive, flat, rows, matched };
};

const TIER_GRID = { display: 'grid', gridTemplateColumns: '1.3fr 1.3fr 1fr 1.2fr 40px', gap: 8, alignItems: 'start' };
const RULE_GRID = { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 40px', gap: 8, alignItems: 'start' };

const LabelWithHint = ({ children, hint }) => (
  <span style={{ fontFamily: 'CustomFont' }}>
    {children}
    <Tooltip title={<span style={{ fontFamily: 'CustomFont' }}>{hint}</span>}>
      <QuestionCircleOutlined style={{ marginInlineStart: 6, color: '#8c8c8c', cursor: 'help' }} />
    </Tooltip>
  </span>
);

const CommissionSimulator = ({ tiers, calculationType, defaultSales = 250000000 }) => {
  const [sales, setSales] = useState(defaultSales);
  const sim = simulateCommission(tiers, Number(sales) || 0);
  const isProgressive = calculationType === 'progressive_tiers';

  const Box = ({ active, title, value, children }) => (
    <div
      className={`rounded-xl border p-3 ${active ? 'border-indigo-400 bg-indigo-50/60 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 opacity-80'}`}
      style={{ fontFamily: 'CustomFont' }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{title}</span>
        {active && <Tag color="geekblue" style={{ margin: 0 }}>روش انتخابی</Tag>}
      </div>
      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatPriceToman(Math.round(value))}</div>
      <div className="text-[11px] text-gray-500 mt-1 leading-5">{children}</div>
    </div>
  );

  return (
    <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-3 mb-4" style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-bold text-gray-700 dark:text-gray-300 text-sm">
          <LabelWithHint hint="با تغییر مبلغ فروش ببینید پورسانت با هر دو روش چقدر می‌شود. این فقط شبیه‌سازی است و جایی ذخیره نمی‌شود.">شبیه‌ساز پورسانت</LabelWithHint>
        </span>
        <InputNumber
          value={sales}
          onChange={setSales}
          min={0}
          addonBefore="مبلغ فروش"
          addonAfter="تومان"
          style={{ width: 280, fontFamily: 'CustomFont' }}
        />
      </div>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Box active={isProgressive} title="پلکانی واقعی" value={sim.progressive}>
            {sim.rows.length > 0 ? sim.rows.map((r, i) => (
              <div key={i}>
                {formatPriceToman(Math.round(r.part))} × {r.rate}٪ = {formatPriceToman(Math.round(r.commission))}
              </div>
            )) : 'پله‌ای تعریف نشده است.'}
          </Box>
        </Col>
        <Col xs={24} md={12}>
          <Box active={!isProgressive} title="درصد نهایی روی کل فروش" value={sim.flat}>
            {sim.matched
              ? <>کل {formatPriceToman(Number(sales) || 0)} در پله {sim.matched.rate}٪ قرار می‌گیرد{sim.matched.bonus ? ` + پاداش ثابت ${formatPriceToman(sim.matched.bonus)}` : ''}.</>
              : 'فروش در هیچ پله‌ای قرار نمی‌گیرد.'}
          </Box>
        </Col>
      </Row>
    </div>
  );
};

const CommissionPlans = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();
  const watchedTiers = Form.useWatch('tiers', form);
  const watchedType = Form.useWatch('calculation_type', form);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await commissionPlansApi.getAll();
      if (res?.data?.commission_plans) {
        setPlans(Array.isArray(res.data.commission_plans) ? res.data.commission_plans : []);
      } else if (res?.commission_plans) {
        setPlans(Array.isArray(res.commission_plans) ? res.commission_plans : []);
      } else if (Array.isArray(res?.data)) {
        setPlans(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching commission plans:', error);
      message.error('خطا در دریافت طرح‌های پورسانت');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await productsApi.getAll();
      if (res?.data?.products) {
        setProducts(res.data.products);
      } else if (res?.products) {
        setProducts(res.products);
      } else if (Array.isArray(res?.data)) {
        setProducts(res.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchProducts();
  }, []);

  const handleCreate = async (values) => {
    setActionLoading(true);
    try {
      const payload = {
        ...values,
        tiers: values.tiers?.map(t => ({
          ...t,
          min_amount: Number(t.min_amount),
          max_amount: t.max_amount ? Number(t.max_amount) : null,
          percentage: Number(t.percentage),
          fixed_bonus: Number(t.fixed_bonus || 0),
        })) || [],
        product_rules: values.product_rules?.map(p => ({
          ...p,
          commission_rate: Number(p.commission_rate),
          min_profit_margin_percent: p.min_profit_margin_percent ? Number(p.min_profit_margin_percent) : null,
        })) || []
      };
      await commissionPlansApi.create(payload);
      message.success('طرح پورسانت جدید با موفقیت ایجاد شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchPlans();
    } catch (error) {
      message.error('خطا در ایجاد طرح پورسانت');
    } finally {
      setActionLoading(false);
    }
  };

  const expandedPlanRow = (plan) => {
    const tiers = plan.tiers || [];
    if (tiers.length === 0) return <span className="text-xs text-gray-400" style={{ fontFamily: 'CustomFont' }}>پله‌ای تعریف نشده است.</span>;
    return (
      <div style={{ fontFamily: 'CustomFont' }}>
        <div className="text-xs text-gray-500 mb-2">
          {plan.calculation_type === 'progressive_tiers'
            ? 'هر بخش از فروش با نرخ پله خودش حساب می‌شود.'
            : 'کل فروش با نرخ پله‌ای که در آن می‌افتد حساب می‌شود.'}
        </div>
        <div className="flex flex-wrap gap-2">
          {tiers.map((t, i) => (
            <Tag key={i} style={{ fontFamily: 'CustomFont' }}>
              {formatPriceToman(Number(t.min_amount) || 0)} تا {t.max_amount ? formatPriceToman(Number(t.max_amount)) : 'بدون سقف'}: {Number(t.percentage)}٪
              {Number(t.fixed_bonus) ? ` + ${formatPriceToman(Number(t.fixed_bonus))}` : ''}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام طرح</span>,
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع محاسبه</span>,
      dataIndex: 'calculation_type',
      key: 'calculation_type',
      render: (type) => (
        <Tag color={type === 'progressive_tiers' ? 'blue' : 'purple'} style={{ fontFamily: 'CustomFont' }}>
          {TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>اعمال بر مبالغ تسویه‌نشده</span>,
      dataIndex: 'applies_to_unsettled',
      key: 'applies_to_unsettled',
      render: (applies) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {applies ? 'بله' : 'خیر'}
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                  <PercentageOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    طرح‌های پورسانت
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    تعریف و مدیریت طرح‌های پورسانت پلکانی و تفکیکی
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPlans} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  طرح پورسانت جدید
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={3} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={plans}
                  rowKey="id"
                  pagination={false}
                  expandable={{ expandedRowRender: expandedPlanRow }}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>تعریف طرح پورسانت جدید</span>}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={760}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ fontFamily: 'CustomFont' }} initialValues={{ applies_to_unsettled: false, calculation_type: 'progressive_tiers', tiers: [{ min_amount: 0, percentage: 0, fixed_bonus: 0 }] }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label={<span style={{ fontFamily: 'CustomFont' }}>نام طرح</span>} rules={[{ required: true, message: 'نام طرح الزامی است' }]}>
                <Input placeholder="مثلاً پورسانت فروش" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="calculation_type"
                label={<LabelWithHint hint="پلکانی: هر بخش از فروش با درصد پله خودش حساب می‌شود (مثلاً ۲٪ برای ۱۰۰ میلیون اول و ۴٪ برای مازاد). درصد کل: کل فروش در یک پله قرار می‌گیرد و درصد همان پله روی کل مبلغ اعمال می‌شود.">نوع محاسبه</LabelWithHint>}
                rules={[{ required: true }]}
              >
                <Select style={{ fontFamily: 'CustomFont' }}>
                  <Option value="progressive_tiers">{TYPE_LABELS.progressive_tiers}</Option>
                  <Option value="fixed_percentage">{TYPE_LABELS.fixed_percentage}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="applies_to_unsettled"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Checkbox style={{ fontFamily: 'CustomFont' }}>
              <LabelWithHint hint="اگر فعال باشد، پورسانت روی مبالغ هنوز تسویه‌نشده معامله هم اعمال می‌شود.">اعمال بر مبالغ تسویه‌نشده</LabelWithHint>
            </Checkbox>
          </Form.Item>

          <div className="mb-2 font-bold text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
            <LabelWithHint hint="هر ردیف یک بازه از ارزش معامله (به تومان) است. پله اول معمولاً از ۰ شروع می‌شود و «تا مبلغ» آخرین پله را خالی بگذارید تا بدون سقف باشد.">پله‌های پورسانت</LabelWithHint>
          </div>
          <Form.List name="tiers">
            {(fields, { add, remove }) => (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 mb-4">
                <div style={{ ...TIER_GRID, marginBottom: 6 }} className="text-xs text-gray-500">
                  <span>از مبلغ (تومان)</span>
                  <span>تا مبلغ (تومان)</span>
                  <span>درصد</span>
                  <LabelWithHint hint="مبلغ ثابتی که علاوه بر درصد پرداخت می‌شود. فقط در نوع «درصد کل بر اساس پله» اعمال می‌شود.">پاداش ثابت</LabelWithHint>
                  <span />
                </div>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={TIER_GRID}>
                    <Form.Item {...restField} name={[name, 'min_amount']} rules={[{ required: true, message: 'الزامی' }]} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="۰" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'max_amount']} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="بدون سقف" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'percentage']} rules={[{ required: true, message: 'الزامی' }]} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="۵" min={0} max={100} addonAfter="%" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'fixed_bonus']} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="اختیاری" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Tooltip title="حذف پله">
                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} disabled={fields.length === 1} />
                    </Tooltip>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ fontFamily: 'CustomFont' }}>
                  افزودن پله جدید
                </Button>
              </div>
            )}
          </Form.List>

          <CommissionSimulator tiers={watchedTiers} calculationType={watchedType} />

          <div className="mb-2 font-bold text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
            <LabelWithHint hint="اگر برای محصولی درصد جدا تعریف کنید، پورسانت آن محصول با همین درصد حساب می‌شود و پله‌های بالا فقط برای بقیه موارد به کار می‌روند.">قوانین پورسانت محصول (اختیاری)</LabelWithHint>
          </div>
          <Form.List name="product_rules">
            {(fields, { add, remove }) => (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 mb-4">
                {fields.length > 0 && (
                  <div style={{ ...RULE_GRID, marginBottom: 6 }} className="text-xs text-gray-500">
                    <span>محصول</span>
                    <span>درصد پورسانت</span>
                    <LabelWithHint hint="اگر حاشیه سود محصول کمتر از این مقدار باشد، پورسانت آن محصول محاسبه نمی‌شود.">حداقل حاشیه سود</LabelWithHint>
                    <span />
                  </div>
                )}
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={RULE_GRID}>
                    <Form.Item {...restField} name={[name, 'product_id']} rules={[{ required: true, message: 'الزامی' }]} style={{ marginBottom: 8 }}>
                      <Select showSearch optionFilterProp="children" placeholder="انتخاب محصول" style={{ fontFamily: 'CustomFont' }}>
                        {products.map(pr => <Option key={pr.id} value={pr.id}>{pr.name}</Option>)}
                      </Select>
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'commission_rate']} rules={[{ required: true, message: 'الزامی' }]} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="۵" min={0} max={100} addonAfter="%" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'min_profit_margin_percent']} style={{ marginBottom: 8 }}>
                      <InputNumber placeholder="اختیاری" min={0} max={100} addonAfter="%" style={{ width: '100%', fontFamily: 'CustomFont' }} />
                    </Form.Item>
                    <Tooltip title="حذف قانون">
                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </Tooltip>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ fontFamily: 'CustomFont' }}>
                  افزودن قانون محصول
                </Button>
              </div>
            )}
          </Form.List>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont' }}>
                ذخیره طرح
              </Button>
              <Button onClick={() => setIsModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CommissionPlans;
